import type { MovementPlan } from '@/modules/game-protocol/ws-payloads.types';

export const MOVEMENT_DISPLAY_DELAY_MS = 150;
const MAX_HISTORY = 16;
const MAX_HORIZON_MS = 3000;
export type MovementPoint = Readonly<{ x: number; y: number }>;
interface AcceptedPlan extends MovementPlan { speed: number }
function correctionTolerance(firstSpeed: number, secondSpeed: number): number {
  // The server integrates nanoseconds; integer wire timestamps quantize them by up to 1ms.
  return Math.max(0.001, Math.max(firstSpeed, secondSpeed) * 0.002);
}

/** The server owns the polyline. This only samples it; it never chooses a target or event. */
export function evaluateMovementPlan(plan: MovementPlan, speed: number, serverTimeMs: number): MovementPoint {
  let distance = plan.moving ? Math.max(0, Math.min(serverTimeMs, plan.validUntilMs) - plan.serverTimeMs) * speed / 1000 : 0;
  let from = plan.points[0]!;
  for (let i = 1; i < plan.points.length; i++) {
    const to = plan.points[i]!;
    const length = Math.hypot(to.x - from.x, to.y - from.y);
    if (length > 0 && distance < length) {
      const fraction = distance / length;
      return { x: from.x + (to.x - from.x) * fraction, y: from.y + (to.y - from.y) * fraction };
    }
    distance -= length;
    from = to;
  }
  return { x: from.x, y: from.y };
}

function completesBufferedPath(previous: AcceptedPlan | undefined, stop: MovementPlan): boolean {
  if (!previous || stop.moving || stop.points.length !== 1) return false;
  const point = stop.points[0]!;
  const tolerance = correctionTolerance(previous.speed, 0);
  // A physical stop can truncate a polyline mid-segment. Compare its timed baseline,
  // including repeated terminal snapshots, rather than requiring the forecast endpoint.
  const projected = evaluateMovementPlan(previous, previous.speed, stop.serverTimeMs);
  return Math.hypot(projected.x - point.x, projected.y - point.y) <= tolerance;
}

export function validMovementPlan(plan: MovementPlan, speed: number, heroId: number): boolean {
  return !!plan && plan.heroId === heroId && Number.isSafeInteger(plan.heroId) &&
    typeof plan.epoch === 'string' && plan.epoch.length > 0 && plan.epoch.length <= 128 &&
    Number.isSafeInteger(plan.sequence) && plan.sequence >= 0 &&
    Number.isFinite(plan.serverTimeMs) && Number.isFinite(plan.validUntilMs) &&
    plan.validUntilMs >= plan.serverTimeMs && plan.validUntilMs - plan.serverTimeMs <= MAX_HORIZON_MS &&
    typeof plan.moving === 'boolean' && (plan.reset === undefined || typeof plan.reset === 'boolean') &&
    Number.isFinite(speed) && speed >= 0 && speed <= 1000 && (!plan.moving || speed > 0) &&
    Array.isArray(plan.points) && plan.points.length >= 1 && plan.points.length <= 8 &&
    plan.points.every(point => !!point && Number.isFinite(point.x) && Number.isFinite(point.y) &&
      Math.abs(point.x) <= 1e7 && Math.abs(point.y) <= 1e7);
}

/** Bounded presentation history and a monotonic performance-clock mapping, owned by GameEngine. */
export class MovementTimeline {
  private plans: AcceptedPlan[] = [];
  private epoch: string | null = null;
  private sequence = -1;
  private latestTimeMs = -Infinity;
  private anchorServerMs = 0;
  private anchorLocalMs = 0;
  private lastClockMs = -Infinity;
  private retiredEpochs: string[] = [];
  private heldPoint: MovementPoint | null = null;
  private lastFramePlan: AcceptedPlan | null = null;

  get active(): boolean { return this.epoch !== null; }
  /** A phase message alone cannot complete the display's currently selected moving plan. */
  get moving(): boolean { return !this.heldPoint && this.selectedPlan()?.moving === true; }
  reset(): void {
    this.plans = []; this.epoch = null; this.sequence = -1; this.latestTimeMs = -Infinity;
    this.lastClockMs = -Infinity; this.retiredEpochs = []; this.heldPoint = null; this.lastFramePlan = null;
  }
  /** Semantic stop discards continuation but retains the ordering guard against stale packets. */
  hold(point: MovementPoint): void { this.plans = []; this.heldPoint = { ...point }; this.lastFramePlan = null; }

  /** Events carry no clock/position: freeze the latest authored baseline, never a UI snapshot. */
  stopAtLatestBaseline(localMs: number): { point: MovementPoint; correction: boolean } | null {
    const latest = this.plans[this.plans.length - 1];
    if (!latest) return this.heldPoint ? { point: { ...this.heldPoint }, correction: false } : null;
    const before = this.sample(localMs);
    const point = { ...latest.points[0]! };
    if (latest.moving) {
      // Keep only the already authored history before the baseline. No prediction beyond a stop.
      this.plans[this.plans.length - 1] = { ...latest, speed: 0, moving: false,
        validUntilMs: latest.serverTimeMs, points: [point] };
    }
    const after = this.sample(localMs);
    return { point, correction: !!(before && after &&
      Math.hypot(before.x - after.x, before.y - after.y) > correctionTolerance(latest.speed, 0)) };
  }

  accept(plan: MovementPlan, speed: number, heroId: number, localMs: number): { accepted: boolean; correction: boolean } {
    if (!Number.isFinite(localMs) || !validMovementPlan(plan, speed, heroId)) return { accepted: false, correction: false };
    const newEpoch = plan.epoch !== this.epoch;
    if (newEpoch && this.epoch !== null && (!plan.reset || this.retiredEpochs.includes(plan.epoch))) {
      return { accepted: false, correction: false };
    }
    if (!newEpoch && (plan.sequence <= this.sequence || (!plan.reset && plan.serverTimeMs < this.latestTimeMs))) {
      return { accepted: false, correction: false };
    }
    const previous = this.sample(localMs);
    const previousPlan = this.plans[this.plans.length - 1];
    const previousSpeed = previousPlan?.speed ?? 0;
    const continuousTerminal = !newEpoch && !plan.reset && completesBufferedPath(previousPlan, plan);
    if (newEpoch || plan.reset) {
      if (newEpoch && this.epoch !== null) this.retiredEpochs = [...this.retiredEpochs.slice(-7), this.epoch];
      this.epoch = plan.epoch;
      this.anchorLocalMs = localMs; this.anchorServerMs = plan.serverTimeMs;
      this.lastClockMs = plan.serverTimeMs - MOVEMENT_DISPLAY_DELAY_MS;
      this.plans = [];
    } else {
      // A 150ms deadband tolerates up to 300ms phase at receipt (display delay + deadband).
      // Accepted sample time is simulation evidence, not a send-wallclock estimate.
      const mappedClock = this.anchorServerMs + localMs - this.anchorLocalMs - MOVEMENT_DISPLAY_DELAY_MS;
      const floor = plan.serverTimeMs - MOVEMENT_DISPLAY_DELAY_MS;
      if (floor > Math.max(this.lastClockMs, mappedClock) + MOVEMENT_DISPLAY_DELAY_MS) this.anchorServerMs += floor - mappedClock;
    }
    this.sequence = plan.sequence; this.latestTimeMs = plan.serverTimeMs;
    const copy: AcceptedPlan = { ...plan, speed, points: plan.points.map(point => ({ ...point })) };
    if (plan.reset || (!plan.moving && !continuousTerminal)) this.plans = [];
    this.heldPoint = null;
    this.plans = [...this.plans, copy];
    const next = this.sample(localMs);
    if (this.plans.length > MAX_HISTORY) {
      // Retain the eligible predecessor until a future replacement reaches the display cursor.
      const selected = this.selectedPlan();
      const tail = this.plans.slice(-(MAX_HISTORY - 1));
      this.plans = selected && selected.serverTimeMs <= this.lastClockMs && !tail.includes(selected)
        ? [selected, ...tail] : this.plans.slice(-MAX_HISTORY);
    }
    const correction = newEpoch || !!plan.reset || (!plan.moving && !continuousTerminal) ||
      !!(previous && next && Math.hypot(previous.x - next.x, previous.y - next.y) > correctionTolerance(speed, previousSpeed));
    if (correction) this.lastFramePlan = null;
    return { accepted: true, correction };
  }

  sample(localMs: number): MovementPoint | null {
    if (this.heldPoint) return { ...this.heldPoint };
    if (!this.plans.length) return null;
    // Local time advances between samples; accepted simulation evidence may raise the mapping in accept().
    this.lastClockMs = Math.max(this.lastClockMs, this.anchorServerMs + localMs - this.anchorLocalMs - MOVEMENT_DISPLAY_DELAY_MS);
    const selected = this.selectedPlan()!;
    return evaluateMovementPlan(selected, selected.speed, this.lastClockMs);
  }

  /** A future replacement may become visible several frames after receipt. Corrections belong to that frame. */
  sampleFrame(localMs: number): { point: MovementPoint | null; correction: boolean } {
    const point = this.sample(localMs);
    const selected = this.heldPoint ? null : this.selectedPlan();
    let correction = false;
    if (selected && this.lastFramePlan && selected !== this.lastFramePlan) {
      const oldIndex = this.plans.indexOf(this.lastFramePlan);
      const selectedIndex = this.plans.indexOf(selected);
      const crossed = oldIndex >= 0 && oldIndex < selectedIndex
        ? this.plans.slice(oldIndex + 1, selectedIndex + 1) : [selected];
      let previous = this.lastFramePlan;
      for (const next of crossed) {
        const expected = evaluateMovementPlan(previous, previous.speed, next.serverTimeMs);
        const actual = next.points[0]!;
        if (Math.hypot(expected.x - actual.x, expected.y - actual.y) > correctionTolerance(previous.speed, next.speed)) {
          correction = true;
        }
        previous = next;
      }
    }
    this.lastFramePlan = selected;
    return { point, correction };
  }

  private selectedPlan(): AcceptedPlan | null {
    let selected = this.plans[0] ?? null;
    for (const plan of this.plans) {
      if (plan.serverTimeMs > this.lastClockMs) break;
      selected = plan;
    }
    return selected;
  }
}
